import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { ApiService } from './api.service';

export interface CustomFieldDef {
  id?: string;
  name: string;
  type: 'text' | 'number' | 'date' | 'email' | 'checkbox';
  // Only on the stored list: marks a global field that was deleted (see GetFieldDefs)
  deleted?: boolean;
}

@Injectable({ providedIn: 'root' })
export class CustomFieldsService {

  constructor(private apiService: ApiService, private alertController: AlertController) { }

  // loaded = false when the request failed, so callers don't mistake "couldn't load" for "no global fields".
  // The stored list also holds { name, deleted: true } entries for global fields that were deleted, so every
  // contact/company that still has a value saved under that name can be cleaned up (see MergeFieldDefsInto).
  // contacts/companies are only the live definitions; deleted_* are the removed names.
  async GetFieldDefs(): Promise<{ contacts: CustomFieldDef[]; companies: CustomFieldDef[]; deleted_contacts: string[]; deleted_companies: string[]; loaded: boolean }> {
    const data: any = await this.apiService.get('firms/custom_field_defs');
    const row = data?.success ? data.data?.[0] : null;
    const contacts = this.SafeParse(row?.contacts_inputs_json);
    const companies = this.SafeParse(row?.companies_inputs_json);
    return {
      contacts: contacts.filter(d => !d.deleted),
      companies: companies.filter(d => !d.deleted),
      deleted_contacts: contacts.filter(d => d.deleted).map(d => d.name),
      deleted_companies: companies.filter(d => d.deleted).map(d => d.name),
      loaded: !!data?.success
    };
  }

  // removed_names = global fields being deleted by this save. They are remembered as deleted entries next to
  // the live ones (and forgotten again if a field with that name is re-added).
  async SaveFieldDefs(entity_type: 'contacts' | 'companies', fields: CustomFieldDef[], removed_names: string[] = []): Promise<{ success: boolean; message: string }> {
    const current = await this.GetFieldDefs();
    // Without the current list we could not keep its deleted entries, so do not overwrite it
    if (!current.loaded) return { success: false, message: "Could not load the current custom fields" };
    const live_names = new Set(fields.map(f => f.name));
    const deleted_names = [...new Set([...(entity_type === 'contacts' ? current.deleted_contacts : current.deleted_companies), ...removed_names])]
      .filter(name => name && !live_names.has(name));
    const stored = [...fields, ...deleted_names.map(name => ({ name, deleted: true }))];

    const body: any = {};
    body[`${entity_type}_inputs_json`] = JSON.stringify(stored);
    const data: any = await this.apiService.put('firms/custom_field_defs', body);
    return { success: !!data?.success, message: data?.message };
  }

  private SafeParse(raw: any): any[] {
    if (!raw) return [];
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  // Derives a stable slug id from each field's name (lowercase, spaces -> underscores) for future
  // use as a dynamic-data/merge-field key - e.g. "Custom Field One" -> "custom_field_one". Fields
  // whose slug collides with an earlier one in the same array get "_2", "_3", ... suffixes, in
  // array order. Recomputed fresh from the current names every time this is called (at save time -
  // see ManageComponent.Save() and each details.page.ts's UpdateBasicInfo()), so a rename always
  // produces a matching id.
  AssignFieldIds(fields: { name: string; id?: string }[]) {
    const used = new Set<string>();
    for (const field of fields) {
      const base = (field.name || '').trim().toLowerCase().replace(/\s+/g, '_');
      if (!base) { field.id = ''; continue; }
      let candidate = base;
      let n = 2;
      while (used.has(candidate)) {
        candidate = `${base}_${n}`;
        n++;
      }
      field.id = candidate;
      used.add(candidate);
    }
  }

  // Adds any firm-wide field definition not already present (by name) into an entity's own
  // custom_data_arr, in place, with a blank value - the existing per-entity add/remove/save UI
  // (addField/removeField in clients & companies details.page.ts) then just works unchanged. A
  // field the entity already has by name keeps its own value (converted if the global type differs).
  // The definition's own id is copied as-is (not regenerated) so a field merged in from the firm
  // list keeps the same dynamic-data key as the definition itself; AssignFieldIds() re-deriving
  // ids at the entity's own save time can still renumber it if that entity has its own separate
  // name collision, but that's the same array-order dedup rule applying locally either way.
  MergeFieldDefsInto(custom_data_arr: any[], field_defs: CustomFieldDef[], defs_loaded = true, deleted_names: string[] = []) {
    if (!custom_data_arr || !field_defs) return;

    // A global field that was deleted from the firm list is removed here (value included) so it stops showing;
    // the removal is saved with the entity's next save. A field counts as deleted when its name is on the firm's
    // deleted list, or it was saved while global (is_global) and is no longer defined. Skipped when the
    // definitions failed to load, otherwise a network error would look like "every global field was deleted".
    if (defs_loaded) {
      const names = new Set(field_defs.map(d => d?.name));
      const deleted = new Set(deleted_names);
      for (let i = custom_data_arr.length - 1; i >= 0; i--) {
        const name = custom_data_arr[i]?.name;
        if (names.has(name)) continue;
        if (custom_data_arr[i]?.is_global || deleted.has(name)) custom_data_arr.splice(i, 1);
      }
    }

    for (const def of field_defs) {
      if (!def?.name) continue;
      const existing = custom_data_arr.find(f => f.name === def.name);
      if (existing) {
        existing.is_global = true;
        // The firm-wide type wins: if this entity still has the field saved under an older type, show it
        // as the global type now. The value is converted, and cleared when it can't be (e.g. text -> number).
        const def_type = def.type || 'text';
        if ((existing.type || 'text') !== def_type) {
          existing.value = this.ConvertValueForType(existing.value, def_type);
          existing.type = def_type;
        }
        continue;
      }
      custom_data_arr.push({
        id: def.id,
        name: def.name,
        type: def.type || 'text',
        value: def.type === 'checkbox' ? false : '',
        is_global: true
      });
    }
  }

  // Carries a stored value over to a different field type. A value that doesn't fit the new type is
  // dropped (blank / unticked) - this is the data loss the "change type" warning in the field manager
  // tells the user about.
  ConvertValueForType(value: any, type: CustomFieldDef['type']): any {
    const empty = value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
    switch (type) {
      case 'checkbox':
        return value === true || value === 'true';
      case 'number':
        return empty || isNaN(Number(value)) || typeof value === 'boolean' ? '' : value;
      case 'date':
        return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && !isNaN(Date.parse(value)) ? value : '';
      case 'email':
      case 'text':
      default:
        return typeof value === 'boolean' || empty ? '' : String(value);
    }
  }

  // Called from UpdateBasicInfo() right before saving. Any field the caller marked _is_new (see
  // addField() in clients/companies details.page.ts) with a real name gets asked, one at a time,
  // whether it should become a firm-wide definition - if so it's appended to the firm's field list
  // right there (a real, separate save to firms.contacts_inputs_json/companies_inputs_json), so it
  // shows up for every other contact/company from then on. Either way the entity keeps its own
  // just-typed value - going global only affects what OTHER entities see going forward, per the
  // same "entity's own value wins" rule MergeFieldDefsInto already follows. Returns the (possibly
  // now-longer) definitions list so the caller can refresh what it uses for IsGlobalField() without
  // a second round trip.
  async PromptAndSaveNewFieldScopes(entity_type: 'contacts' | 'companies', custom_data_arr: any[]): Promise<CustomFieldDef[]> {
    // This is a best-effort extra step before the caller's own save, so it must never throw:
    // any failure falls back to treating new fields as local and returns what it knows.
    let currentDefs: CustomFieldDef[] = [];
    try {
      if (!Array.isArray(custom_data_arr)) return currentDefs;
      currentDefs = (await this.GetFieldDefs())[entity_type] || [];
      const new_fields = custom_data_arr.filter(f => f && f._is_new && typeof f.name === 'string' && f.name.trim());
      if (new_fields.length) {
        for (const field of new_fields) {
          try {
            const makeGlobal = await this.PromptFieldScope(entity_type, field.name);
            if (makeGlobal) field.is_global = true;
            if (makeGlobal && !currentDefs.some(d => d.name === field.name)) {
              const updatedDefs = [...currentDefs, { name: field.name, type: field.type }];
              const result = await this.SaveFieldDefs(entity_type, updatedDefs);
              if (result.success) currentDefs = updatedDefs;
              else field.is_global = false;
            }
          } catch (e) {
            console.error('Could not save new custom field scope', e);
            field.is_global = false;
          }
        }
      }
    } catch (e) {
      console.error('PromptAndSaveNewFieldScopes failed', e);
    }
    try {
      if (Array.isArray(custom_data_arr)) custom_data_arr.forEach(f => { if (f) delete f._is_new; });
    } catch (e) { }
    return currentDefs;
  }

  private async PromptFieldScope(entity_type: 'contacts' | 'companies', field_name: string): Promise<boolean> {
    const label = entity_type === 'contacts' ? 'contact' : 'company';
    return new Promise<boolean>(async (resolve) => {
      const alert = await this.alertController.create({
        header: 'New Custom Field',
        message: `Should "${field_name}" apply to just this ${label}, or to every ${label} (a global field)?`,
        backdropDismiss: false,
        buttons: [
          { text: `Just This ${label.charAt(0).toUpperCase() + label.slice(1)}`, role: 'cancel', handler: () => resolve(false) },
          { text: 'Make It Global', handler: () => resolve(true) }
        ]
      });
      await alert.present();
    });
  }
}
